import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhTrash";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "../message-status-badge/message-status-badge";
import type { MessageStatus } from "../message-status-badge/message-status-badge";

import { WowSortableTable } from "ui-components/wow-base-table/wow-sortable-table";
import type { CreatorMessage } from "api-contracts/rpc/get-creator-messages";
import type { TableColumn } from "ui-components/wow-base-table/types";
import type { ISortableState, ISortableService } from "ui-components/wow-base-table/sort-types";
import { inject } from "ui-common/di";
import type { ICreatorMessagesState, ICreatorMessagesService } from "../../services/creator-messages/types";
import { styles as baseStyles } from "ui-components/wow-base-table/wow-base-table.css";
import { styles } from "./messages-table.css";
import { t } from "../../translations/i18n";

@customElement("messages-table")
export class MessagesTable extends WowSortableTable<CreatorMessage> {
  static override styles = [baseStyles, styles];

  @inject<ICreatorMessagesState>("creatorMessagesState")
  private messagesState!: ICreatorMessagesState;

  @inject<ICreatorMessagesService>("creatorMessagesService")
  private messagesService!: ICreatorMessagesService;

  @property({ type: Array })
  messages: CreatorMessage[] = [];

  protected getSortState(): ISortableState<CreatorMessage> {
    return this.messagesState;
  }

  protected getSortService(): ISortableService {
    return this.messagesService;
  }

  protected getItems(): CreatorMessage[] {
    return this.messages;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.disableRowClick = true;
    this.hoverable = false;
    this.tileRender = (message) => this.renderMessageTile(message);
  }

  override willUpdate(changed: Map<string, unknown>) {
    super.willUpdate(changed);
    this.columns = this.buildColumns();
  }

  private buildColumns(): TableColumn<CreatorMessage>[] {
    return [
      {
        key: "title",
        header: t("messagesPage.columnSubject"),
        render: (_value, row) => html`<span class="message-title">${row.title}</span>`,
      },
      {
        key: "status",
        header: t("messagesPage.columnStatus"),
        sortable: true,
        render: (_value, row) => this.renderMessageStatusBadge(row.status),
      },
      {
        key: "created",
        header: t("messagesPage.columnDate"),
        sortable: true,
        render: (_value, row) => this.renderDate(row.sent_at ?? row.created),
      },
      {
        key: "sent_time",
        header: t("messagesPage.columnTime"),
        render: (_value, row) => this.renderTime(row.sent_at ?? row.created),
      },
      {
        key: "delivered_count",
        header: t("messagesPage.columnDelivered"),
        render: (_value, row) => (row.status === "sent" || row.status === "sending" ? String(row.delivered_count) : ""),
      },
      {
        key: "opened_count",
        header: t("messagesPage.columnOpens"),
        render: (_value, row) => (row.status === "sent" || row.status === "sending" ? String(row.opened_count) : ""),
      },
      {
        key: "clicked_count",
        header: t("messagesPage.columnClicks"),
        render: (_value, row) => (row.status === "sent" || row.status === "sending" ? String(row.clicked_count) : ""),
      },
      {
        key: "action",
        header: "",
        render: (_value, row) => this.renderActionButton(row),
      },
      {
        key: "menu",
        header: "",
        render: (_value, row) => this.renderMenu(row),
      },
    ];
  }

  private renderMessageStatusBadge(status: string) {
    return html`<message-status-badge .status=${status as MessageStatus}></message-status-badge>`;
  }

  private renderActionButton(message: CreatorMessage) {
    if (message.status === "draft" || message.status === "scheduled") {
      return html`
        <wow-button variant="neutral" size="small" rounded @click=${() => this.emit("edit-message", { message })}>
          <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
          ${t("messagesPage.editMessage")}
        </wow-button>
      `;
    }
    if (message.status === "sent" || message.status === "sending" || message.status === "archived") {
      return html`
        <wow-button variant="neutral" size="small" rounded @click=${() => this.emit("view-message", { message })}>
          <ph-eye slot="prefix" size="16"></ph-eye>
          ${t("messagesPage.viewMessage")}
        </wow-button>
      `;
    }
    return html``;
  }

  private renderMenu(message: CreatorMessage) {
    const s = message.status;

    if (s === "sent" || s === "sending" || s === "draft" || s === "scheduled") {
      return this.renderDropdownMenu(message, [
        { action: "duplicate", icon: "ph-copy", label: "messagesPage.duplicateMessage" as const },
        {
          action: "archive",
          icon: "ph-archive",
          label: "messagesPage.archiveMessage" as const,
          variant: "danger" as const,
        },
      ]);
    }

    if (s === "archived") {
      return this.renderDropdownMenu(message, [
        { action: "duplicate", icon: "ph-copy", label: "messagesPage.duplicateMessage" as const },
      ]);
    }

    return html``;
  }

  private renderDropdownMenu(
    message: CreatorMessage,
    items: { action: string; icon: string; label: Parameters<typeof t>[0]; variant?: "danger" }[],
  ) {
    return html`
      <dropdown-menu
        glass
        placement="bottom-end"
        @dropdown-item-click=${(e: CustomEvent) => this.handleMenuAction(e, message)}
      >
        <wow-button
          slot="trigger"
          variant="subtle"
          size="medium"
          rounded
          .borderColor=${"var(--sds-color-border-default-secondary)"}
          aria-label="More options"
        >
          <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
        </wow-button>
        ${items.map(
          (item) => html`
            <dropdown-menu-item action=${item.action} variant=${item.variant ?? "default"} icon-position="start">
              ${this.renderMenuIcon(item.icon)} ${t(item.label)}
            </dropdown-menu-item>
          `,
        )}
      </dropdown-menu>
    `;
  }

  private renderMenuIcon(icon: string) {
    switch (icon) {
      case "ph-copy":
        return html`<ph-copy slot="icon" size="16"></ph-copy>`;
      case "ph-note-pencil":
        return html`<ph-note-pencil slot="icon" size="16"></ph-note-pencil>`;
      case "ph-archive":
        return html`<ph-archive slot="icon" size="16"></ph-archive>`;
      default:
        return nothing;
    }
  }

  private renderMessageTile(message: CreatorMessage) {
    return html`
      <div class="message-tile">
        <div class="message-tile-header">
          <span class="message-tile-title">${message.title}</span>
          ${this.renderMessageStatusBadge(message.status)}
        </div>
        <div class="message-tile-details">
          <span>${this.renderDate(message.sent_at ?? message.created)}</span>
          <span>${this.renderTime(message.sent_at ?? message.created)}</span>
          ${message.status === "sent" || message.status === "sending"
            ? html`
                <span>${t("messagesPage.columnDelivered")}: ${message.delivered_count}</span>
                <span>${t("messagesPage.columnOpens")}: ${message.opened_count}</span>
              `
            : nothing}
        </div>
        <div class="message-tile-actions">${this.renderTileActionButton(message)} ${this.renderMenu(message)}</div>
      </div>
    `;
  }

  private renderTileActionButton(message: CreatorMessage) {
    if (message.status === "draft" || message.status === "scheduled") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("edit-message", { message })}
        >
          <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
          ${t("messagesPage.editMessage")}
        </wow-button>
      `;
    }
    if (message.status === "sent" || message.status === "sending" || message.status === "archived") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("view-message", { message })}
        >
          <ph-eye slot="prefix" size="16"></ph-eye>
          ${t("messagesPage.viewMessage")}
        </wow-button>
      `;
    }
    return nothing;
  }

  private handleMenuAction(e: CustomEvent, message: CreatorMessage) {
    const action = e.detail?.action;
    switch (action) {
      case "edit":
        this.emit("edit-message", { message });
        break;
      case "view":
        this.emit("view-message", { message });
        break;
      case "duplicate":
        this.emit("duplicate-message", { message });
        break;
      case "archive":
        this.emit("archive-message", { messageId: message.id });
        break;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "messages-table": MessagesTable;
  }
}
